import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { useTranslation } from "react-i18next";
import {
  pagePrimaryButtonSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";
import {
  bob,
  cloudLift,
  motionSafe,
  rise,
  studioDialogBackdropSx,
  studioDialogPaperSx,
} from "../AI/studioDialogMotion";

const ACCENT = "#00897b";

function ProjectStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 92,
        mt: 1.25,
        mb: 0.5,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      {/* Trunk / season node */}
      <Box
        sx={{
          position: "absolute",
          left: "18%",
          top: 18,
          width: 44,
          height: 52,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 24, color: accent }} />
      </Box>
      {/* Client node */}
      <Box
        sx={{
          position: "absolute",
          left: "42%",
          top: 28,
          width: 44,
          height: 44,
          borderRadius: 1.25,
          bgcolor: alpha(accent, 0.12),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2.5s ease-in-out infinite`,
          animationDelay: "0.15s",
          ...motionSafe,
        }}
      >
        <BusinessOutlinedIcon sx={{ fontSize: 22, color: accent }} />
      </Box>
      {/* Project leaf */}
      <Box
        sx={{
          position: "absolute",
          right: "14%",
          top: 14,
          width: 54,
          height: 54,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.16),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.18)}`,
          animation: `${cloudLift} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <AccountTreeOutlinedIcon sx={{ fontSize: 28, color: accent }} />
      </Box>
      {/* Connecting dots */}
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${32 + i * 10}%`,
            bottom: 16,
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: alpha(accent, 0.35 + i * 0.15),
            animation: `${bob} ${1.8 + i * 0.2}s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Dialog Compás: crear proyecto (cliente + temporada nuevos).
 * Misma familia visual que ficha / Drive (blur + rise + stage animado).
 */
export default function SkillAgentNewProjectDialog({
  open,
  onClose,
  form,
  onFormChange,
  customerOptions = [],
  seasonChips = [],
  saving = false,
  error = "",
  onClearError,
  onSubmit,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = ACCENT;

  const fieldSx = {
    ...pageFieldSx,
    "& .MuiOutlinedInput-root": {
      bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#000", 0.02),
    },
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={180}
      slotProps={{
        backdrop: { sx: studioDialogBackdropSx },
      }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{
        sx: {
          ...studioDialogPaperSx,
          animation: open ? `${rise} 280ms ease both` : undefined,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(
            accent,
            0.02,
          )} 55%)`,
          animation: `${rise} 300ms ease both`,
          ...motionSafe,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {t("skillAgent.dialog.newProjectTitle", {
            defaultValue: "Nuevo proyecto",
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("skillAgent.dialog.newProjectLead", {
            defaultValue:
              "Crea una rama nueva en Compás: nombre, cliente y temporada (puedes escribir valores nuevos).",
          })}
        </Typography>
        <ProjectStage accent={accent} />
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={1.5}>
          {error ? (
            <Alert severity="error" onClose={onClearError}>
              {error}
            </Alert>
          ) : null}
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t("skillAgent.dialog.newProjectName", {
              defaultValue: "Nombre del proyecto",
            })}
            placeholder="Arteixo AW27"
            value={form?.Name || ""}
            onChange={(e) =>
              onFormChange?.((p) => ({ ...p, Name: e.target.value }))
            }
            disabled={saving}
            sx={fieldSx}
          />
          <Autocomplete
            freeSolo
            options={customerOptions}
            value={form?.customer || null}
            inputValue={form?.customerInput || ""}
            onChange={(_, value) => {
              if (typeof value === "string") {
                onFormChange?.((p) => ({
                  ...p,
                  customer: null,
                  customerInput: value,
                }));
              } else {
                onFormChange?.((p) => ({
                  ...p,
                  customer: value,
                  customerInput: value?.label || "",
                }));
              }
            }}
            onInputChange={(_, value) =>
              onFormChange?.((p) => ({
                ...p,
                customerInput: value,
                customer:
                  p.customer && p.customer.label === value ? p.customer : null,
              }))
            }
            getOptionLabel={(opt) =>
              typeof opt === "string" ? opt : opt?.label || ""
            }
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            disabled={saving}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("skillAgent.dialog.newProjectClient", {
                  defaultValue: "Cliente",
                })}
                placeholder={t("skillAgent.dialog.newProjectClientPh", {
                  defaultValue: "Elige o escribe un cliente nuevo",
                })}
                sx={fieldSx}
              />
            )}
          />
          <TextField
            fullWidth
            size="small"
            label={t("skillAgent.dialog.seasonLabel", {
              defaultValue: "Temporada",
            })}
            placeholder="AW2027"
            value={form?.Temporada || ""}
            onChange={(e) =>
              onFormChange?.((p) => ({ ...p, Temporada: e.target.value }))
            }
            disabled={saving}
            sx={fieldSx}
          />
          {seasonChips.length ? (
            <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
              {seasonChips.slice(0, 12).map((s) => {
                const selected =
                  String(form?.Temporada || "")
                    .trim()
                    .toUpperCase() === String(s).toUpperCase();
                return (
                  <Chip
                    key={s}
                    size="small"
                    label={s}
                    onClick={() =>
                      onFormChange?.((p) => ({ ...p, Temporada: s }))
                    }
                    sx={{
                      fontWeight: 700,
                      bgcolor: selected ? alpha(accent, 0.18) : undefined,
                      borderColor: selected ? accent : undefined,
                      color: selected ? accent : undefined,
                      border: "1px solid",
                      transition: "transform 0.12s ease",
                      "&:hover": { transform: "translateY(-1px)" },
                    }}
                    variant={selected ? "filled" : "outlined"}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          {t("skillAgent.common.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={saving}
          sx={{
            ...pagePrimaryButtonSx,
            textTransform: "none",
            fontWeight: 700,
            bgcolor: accent,
            "&:hover": { bgcolor: alpha(accent, 0.88) },
            minWidth: 96,
          }}
        >
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            t("skillAgent.common.create", { defaultValue: "Crear" })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
